import VoiceMicButton from '@/components/VoiceMicButton';
import { Checkbox } from '@/components/ui/Checkbox';
import { FormField } from '@/components/ui/FormField';
import { Input, NativeSelect, Textarea } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { SymptomPicker } from './SymptomPicker';
import { cn } from '@/lib/cn';
import { emptyValue, maxLengthOf, type SymptomField, type SymptomValue } from './symptomForm';

interface SymptomFormFieldsProps {
  fields: readonly SymptomField[];
  values: Record<string, SymptomValue>;
  onChange: (key: string, value: SymptomValue) => void;
  /** Field keys to mark as missing (mandatory, left blank, after a save attempt). */
  missing?: ReadonlySet<string>;
  disabled?: boolean;
}

/**
 * Renders the admin-configured symptom fields (Admin → Clinical → Form
 * Customization) in their configured order. Text fields keep the voice mic
 * the old single "Symptoms / complaints" box had.
 */
export function SymptomFormFields({ fields, values, onChange, missing, disabled }: SymptomFormFieldsProps) {
  return (
    <div className="flex flex-col gap-3">
      {fields.map((f) => {
        const id = `symptom-${f.field_key}`;
        const value = values[f.field_key] ?? emptyValue(f.field_type);
        const text = typeof value === 'string' ? value : '';
        // Typing stops at the limit (maxLength); text carried in by voice or
        // from intake can still exceed it, so that is flagged, never cut.
        const max = maxLengthOf(f);
        const tooLong = max !== null && text.trim().length > max;
        const isMissing = !!missing?.has(f.field_key);
        const invalid = isMissing || tooLong;
        const error = isMissing ? `${f.label} is required`
          : tooLong ? `${f.label} must be ${max} characters or fewer — shorten it` : undefined;
        const list = Array.isArray(value) ? value : [];
        const mic = (f.field_type === 'text' || f.field_type === 'textarea') && !disabled
          ? <VoiceMicButton onText={(t) => onChange(f.field_key, text ? `${text} ${t}` : t)} />
          : undefined;

        if (f.field_type === 'checkbox') {
          return (
            <div key={f.field_key}>
              <label htmlFor={id} className="flex items-center gap-2 text-[13px] text-ink">
                <Checkbox id={id} checked={value === true} disabled={disabled} onCheckedChange={(c) => onChange(f.field_key, c === true)} />
                <span>{f.label}{f.mandatory && <span className="text-danger"> *</span>}</span>
              </label>
              {error && <div className="mt-[3px] text-[11px] text-danger">{error}</div>}
              {!error && f.help_text && <div className="mt-1 text-[11.5px] text-muted-foreground">{f.help_text}</div>}
            </div>
          );
        }

        let control;
        switch (f.field_type) {
          case 'multiselect':
            control = (
              <SymptomPicker id={id} options={f.options} value={list} onChange={(v) => onChange(f.field_key, v)}
                allowCustom={f.allow_custom} placeholder={f.placeholder || undefined} invalid={invalid} disabled={disabled} />
            );
            break;
          case 'checkboxes':
            control = (
              <SegmentedControl multiple size="sm" disabled={disabled}
                options={f.options.map((o) => ({ value: o, label: o }))}
                value={list} onChange={(v) => onChange(f.field_key, v)} />
            );
            break;
          case 'radio':
            control = (
              <SegmentedControl size="sm" disabled={disabled}
                options={f.options.map((o) => ({ value: o, label: o }))}
                value={text || null} onChange={(v) => onChange(f.field_key, v === text ? '' : v)} />
            );
            break;
          case 'dropdown':
            control = (
              <NativeSelect id={id} className="w-full" value={text} invalid={invalid} disabled={disabled}
                onChange={(e) => onChange(f.field_key, e.target.value)}>
                <option value="">{f.placeholder || 'Select'}</option>
                {/* A value recorded from an option the admin has since removed still shows. */}
                {text && !f.options.includes(text) && <option value={text}>{text}</option>}
                {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
              </NativeSelect>
            );
            break;
          case 'textarea':
            control = (
              <Textarea id={id} value={text} rows={2} maxLength={max ?? undefined} invalid={invalid} disabled={disabled}
                placeholder={f.placeholder || undefined} onChange={(e) => onChange(f.field_key, e.target.value)} />
            );
            break;
          case 'number':
            control = (
              <Input id={id} type="number" value={text} invalid={invalid} disabled={disabled}
                placeholder={f.placeholder || undefined} onChange={(e) => onChange(f.field_key, e.target.value)} />
            );
            break;
          case 'date':
            control = (
              <Input id={id} type="date" value={text} invalid={invalid} disabled={disabled}
                onChange={(e) => onChange(f.field_key, e.target.value)} />
            );
            break;
          default:
            control = (
              <Input id={id} value={text} maxLength={max ?? undefined} invalid={invalid} disabled={disabled}
                placeholder={f.placeholder || undefined} onChange={(e) => onChange(f.field_key, e.target.value)} />
            );
        }

        return (
          <FormField key={f.field_key} label={f.label} required={f.mandatory} error={error} htmlFor={id}
            labelExtra={mic} hint={f.help_text || undefined}>
            {control}
            {/* Counter only when the admin set a limit for this field. */}
            {f.max_length != null && max !== null && (
              <div aria-live="polite" className={cn('mt-0.5 text-right text-[11px]', tooLong ? 'text-danger' : 'text-ink-muted')}>
                {text.trim().length} / {max}
              </div>
            )}
          </FormField>
        );
      })}
    </div>
  );
}
